/**
 * MRL Cart Bridge
 * Shared cart API for theme sections.
 *
 * Use from any section:
 *   await window.MRLCart.addItem(variantId, 1, { openDrawer: true });
 *   await window.MRLCart.addItems([{ id: variantId, quantity: 1 }], { openDrawer: true });
 *   await window.MRLCart.changeItem(lineItemKey, quantity, { refreshDrawer: true });
 */
(function () {
  if (window.MRLCart) return;

  function root() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  function getDrawer() {
    return document.querySelector('.cart-drawer');
  }

  function getDrawerSection() {
    return document.querySelector('#shopify-section-cart-drawer');
  }

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }

    return `$${(Number(cents || 0) / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  }

  function openDrawer() {
    const drawer = getDrawer();
    if (!drawer) return false;

    drawer.classList.add('cart-drawer--active');
    document.body.style.overflowY = 'hidden';
    return true;
  }

  function closeDrawer() {
    const drawer = getDrawer();
    if (!drawer) return false;

    drawer.classList.remove('cart-drawer--active');
    document.body.style.overflowY = '';
    return true;
  }

  async function refreshDrawer() {
    const currentSection = getDrawerSection();
    if (!currentSection) return false;

    const response = await fetch(root() + '?section_id=cart-drawer', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      cache: 'no-store'
    });

    if (!response.ok) return false;

    const html = await response.text();
    const nextSection = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('#shopify-section-cart-drawer');

    if (!nextSection) return false;

    currentSection.innerHTML = nextSection.innerHTML;
    document.dispatchEvent(new CustomEvent('mrl:cart:drawer-refreshed', { bubbles: true }));
    return true;
  }

  async function getCart() {
    const response = await fetch(root() + 'cart.js', {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      cache: 'no-store'
    });

    if (!response.ok) throw new Error('Cart fetch failed');
    return response.json();
  }

  function updateVisibleCartTotals(cart) {
    if (!cart) return;

    document.querySelectorAll('.total_amount').forEach((el) => {
      el.textContent = formatMoney(cart.total_price);
    });

    document.querySelectorAll('.bag_value').forEach((el) => {
      el.textContent = `(${cart.item_count} items)`;
    });
  }

  async function addItem(variantId, quantity, options) {
    const opts = Object.assign({ refreshDrawer: true, openDrawer: false }, options || {});

    const response = await fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        id: Number(variantId),
        quantity: Number(quantity || 1)
      })
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Cart add failed');
    }

    let cart = null;
    try {
      cart = await getCart();
      updateVisibleCartTotals(cart);
    } catch (error) {
      console.warn('[MRL Cart] Cart fetch after add failed:', error);
    }

    if (opts.refreshDrawer) await refreshDrawer();
    if (opts.openDrawer) openDrawer();

    document.dispatchEvent(new CustomEvent('mrl:cart:updated', {
      bubbles: true,
      detail: { action: 'addItem', item: data, cart: cart }
    }));

    return data;
  }

  async function addItems(items, options) {
    const opts = Object.assign({ refreshDrawer: true, openDrawer: false }, options || {});

    const normalizedItems = (items || [])
      .filter((item) => item && item.id)
      .map((item) => ({
        id: Number(item.id),
        quantity: Number(item.quantity || 1)
      }));

    if (!normalizedItems.length) {
      throw new Error('No cart items provided');
    }

    const response = await fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items: normalizedItems })
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Cart add failed');
    }

    let cart = null;
    try {
      cart = await getCart();
      updateVisibleCartTotals(cart);
    } catch (error) {
      console.warn('[MRL Cart] Cart fetch after add-items failed:', error);
    }

    if (opts.refreshDrawer) await refreshDrawer();
    if (opts.openDrawer) openDrawer();

    document.dispatchEvent(new CustomEvent('mrl:cart:updated', {
      bubbles: true,
      detail: { action: 'addItems', items: data, cart: cart }
    }));

    return data;
  }

  async function changeItem(lineItemKey, quantity, options) {
    const opts = Object.assign({ refreshDrawer: false, openDrawer: false }, options || {});

    const response = await fetch(root() + 'cart/change.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        id: lineItemKey,
        quantity: Number(quantity)
      })
    });

    const cart = await response.json().catch(() => ({}));
    if (!response.ok || cart.status) {
      throw new Error(cart.description || cart.message || 'Cart change failed');
    }

    updateVisibleCartTotals(cart);
    if (opts.refreshDrawer) await refreshDrawer();
    if (opts.openDrawer) openDrawer();

    document.dispatchEvent(new CustomEvent('mrl:cart:updated', {
      bubbles: true,
      detail: { action: 'changeItem', cart: cart }
    }));

    return cart;
  }

  window.MRLCart = {
    root,
    getCart,
    addItem,
    addItems,
    changeItem,
    refreshDrawer,
    openDrawer,
    closeDrawer,
    updateVisibleCartTotals,
    formatMoney
  };

  // Backward-compatible alias for code already migrated during cleanup.
  window.MRLCartDrawer = window.MRLCartDrawer || {
    open: openDrawer,
    close: closeDrawer,
    refresh: refreshDrawer,
    getCart: getCart,
    changeItem: changeItem,
    add: function (variantId, quantity) {
      return addItem(variantId, quantity || 1, {
        refreshDrawer: true,
        openDrawer: true
      });
    },
    addItems: function (items) {
      return addItems(items, {
        refreshDrawer: true,
        openDrawer: true
      });
    }
  };
})();
